import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import axios from 'axios';
import { FaTimes, FaEdit, FaTrash, FaPlus, FaSearch, FaFilter } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { refreshData, fetchMasterData } from '../../store/slices/masterDataSlice';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useConfirm } from '../../components/useConfirm';
import CustomConfirm from '../../components/CustomConfirm';
import { API_BASE_URL } from '../../utils/apiConfig';
import { CourseSelect } from '../../components/MasterDataSelect';

const Container = styled.div`
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 15px 10px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 5px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const tableGridColumns = `
  40px
  minmax(70px, 1fr)
  minmax(120px, 1.5fr)
  minmax(140px, 1.5fr)
  minmax(90px, 1fr)
  minmax(150px, 2fr)
  minmax(100px, 1fr)
  minmax(100px, 1fr)
  minmax(70px, 0.8fr)
  minmax(70px, 0.8fr)
  120px
`;

const TableContainer = styled.div`
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 6px;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${tableGridColumns};
  width: 100%;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #333;
  
  @media (max-width: 768px) {
    display: none; /* Hide header on mobile */
  }
`;

const TableBody = styled.div`
  width: 100%;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    overflow-x: visible;
  }
`;

const HeaderCell = styled.div`
  padding: 15px 8px;
  border-right: 1px solid #dee2e6;
  font-size: 12px;
  
  &:last-child {
    border-right: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${tableGridColumns};
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    display: block !important; /* Force block display */
    grid-template-columns: none !important; /* Remove grid */
    border: 1px solid #eee;
    border-radius: 8px;
    margin-bottom: 15px;
    padding: 15px;
    background: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    
    /* Ensure proper spacing for mobile card layout */
    > * {
      margin-bottom: 8px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    /* Ensure action buttons cell doesn't inherit grid properties */
    > *:last-child {
      display: block !important;
      width: 100% !important;
    }
  }
`;

const TableCell = styled.div`
  padding: 12px 8px;
  border-right: 1px solid #eee;
  font-size: 12px;
  color: #333;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
    padding: 10px 0;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:before {
      content: attr(data-label) ": ";
      font-weight: 600;
      color: #555;
      display: inline-block;
      width: 120px;
      margin-right: 10px;
    }
  }
`;

const CheckboxCell = styled(TableCell)`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none; /* Hide select checkbox on mobile */
  }
`;

const NameCell = styled(TableCell)`
  color: #007bff;
  font-weight: 500;
  
  @media (max-width: 768px) {
    &:before {
      content: "Name: ";
    }
  }
`;

const EmailCell = styled(TableCell)`
  color: #007bff;
  
  @media (max-width: 768px) {
    &:before {
      content: "Email: ";
    }
  }
`;



const CourseCell = styled(TableCell)`
  color: #007bff;
  
  @media (max-width: 768px) {
    &:before {
      content: "Course: ";
    }
  }
`;

const DateOfBirthCell = styled(TableCell)`
  color: #495057;
  font-weight: 500;
  
  @media (max-width: 768px) {
    &:before {
      content: "Date of Birth: ";
    }
  }
`;

const ContactNumberCell = styled(TableCell)`
  color: #495057;
  
  @media (max-width: 768px) {
    &:before {
      content: "Contact Number: ";
    }
  }
`;

const InstructorIdCell = styled(TableCell)`
  font-weight: 500;
  color: #495057;
  
  @media (max-width: 768px) {
    &:before {
      content: "Instructor ID: ";
    }
  }
`;

const StatusCell = styled(TableCell)`
  color: #28a745;
  font-weight: 500;
  
  @media (max-width: 768px) {
    &:before {
      content: "Status: ";
    }
  }
`;

const ActionsCell = styled(TableCell)`
  @media (max-width: 768px) {
    &:before {
      content: "Actions: ";
    }
    
    /* Force inline display for action buttons */
    display: flex !important;
    flex-direction: row !important;
    gap: 8px;
    align-items: center;
    flex-wrap: nowrap;
    
    /* Override any conflicting styles */
    width: auto !important;
    min-width: auto !important;
    text-align: left !important;
    
    /* Ensure buttons are in the same row */
    > * {
      display: inline-block !important;
      float: none !important;
      vertical-align: middle;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    /* Force horizontal layout */
    > button {
      display: inline-block !important;
      float: none !important;
      clear: none !important;
      margin-right: 8px !important;
      
      &:last-child {
        margin-right: 0 !important;
      }
    }
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  
  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    overflow-x: visible;
  }
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  margin: 0 4px;
  border: none;
  border-radius: 4px;
  background: ${props => props.danger ? '#dc3545' : 'rgb(51, 66, 147)'};
  color: white;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: ${props => props.danger ? '#c82333' : 'rgb(5, 71, 141)'};
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.85rem;
    margin: 0; /* Remove margin since ActionsCell handles spacing */
    min-width: 40px;
    height: 36px;
    display: inline-flex !important; /* Force inline display */
    align-items: center;
    justify-content: center;
    flex-shrink: 0; /* Prevent button shrinking */
    float: none !important; /* Ensure no floating */
    clear: none !important; /* Clear any floating */
  }
  
  @media (max-width: 480px) {
    padding: 10px 14px;
    font-size: 0.9rem;
    min-width: 44px;
    height: 40px;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 15px 15px 0 0;
`;

const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #e9ecef;
    color: #2c3e50;
  }
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.9rem;
  
  &:after {
    content: ${props => props.required ? '" *"' : '""'};
    color: #dc3545;
  }
`;

const Select = styled.select`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const ModalFooter = styled.div`
  padding: 20px 30px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 15px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary && `
    background: rgb(51, 66, 147);
    color: white;
    
    &:hover {
      background: rgb(5, 71, 141);
    }
  `}
  
  ${props => props.secondary && `
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #545b62;
    }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
`;

const SignaturePadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  grid-column: 1 / -1;
`;

const SignatureCanvas = styled.canvas`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: crosshair;
  background: #f8f9fa;
  width: 300px;
  height: 150px;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ClearSignatureButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  
  &:hover {
    background: #545b62;
  }
`;

const SignaturePreview = styled.img`
  width: 300px;
  height: 300px;
  object-fit: contain;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 8px;
  background: #f8f9fa;
`;

// Add CSS for spinner animation
const GlobalStyles = createGlobalStyle`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AllInstructors = () => {
  const dispatch = useDispatch();
  const { instructors: masterInstructors } = useSelector(state => state.masterData);
  const { courses: masterCourses } = useSelector(state => state.masterData);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editApiMessage, setEditApiMessage] = useState(null);
  const [editApiError, setEditApiError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const signatureCanvasRef = useRef(null);
  const [signatureContext, setSignatureContext] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const { confirmState, showDeleteConfirm } = useConfirm();

  // Initialize signature canvas
  const initSignatureCanvas = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    canvas.style.width = '300px';
    canvas.style.height = '150px';
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
      ctx.strokeStyle = '#1a202c';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      setSignatureContext(ctx);
    }
  }, []);

  useEffect(() => {
    if (editModalOpen) {
      initSignatureCanvas();
      window.addEventListener('resize', initSignatureCanvas);
      return () => window.removeEventListener('resize', initSignatureCanvas);
    }
  }, [editModalOpen, initSignatureCanvas]);

  // Autofill signature pad when signature data is available
  useEffect(() => {
    if (!editModalOpen || !editForm?.signature) return;
    
    // Use a small delay to ensure canvas is initialized
    const timeoutId = setTimeout(() => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      const img = new Image();
      img.onload = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
        
        // Draw the signature image on the canvas
        const scale = Math.min(
          (canvas.width / ratio) / img.width,
          (canvas.height / ratio) / img.height
        );
        const x = ((canvas.width / ratio) - img.width * scale) / 2;
        const y = ((canvas.height / ratio) - img.height * scale) / 2;
        
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        
        // Update the context state to ensure drawing works after autofill
        setSignatureContext(ctx);
      };
      img.onerror = () => {
        console.error('Error loading signature image');
      };
      img.src = editForm.signature;
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [editForm?.signature, editModalOpen]);

  const startSignatureDrawing = (e) => {
    if (!signatureContext) return;
    setIsDrawing(true);
    signatureContext.beginPath();
    signatureContext.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
  };

  const drawSignature = (e) => {
    if (!isDrawing || !signatureContext) return;
    signatureContext.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    signatureContext.stroke();
  };

  const stopSignatureDrawing = () => {
    if (!signatureContext) return;
    setIsDrawing(false);
    const signatureDataUrl = signatureCanvasRef.current.toDataURL();
    setEditForm(prev => ({ ...prev, signature: signatureDataUrl }));
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!signatureContext || !canvas) return;
    signatureContext.clearRect(0, 0, canvas.width, canvas.height);
    signatureContext.fillStyle = '#ffffff';
    signatureContext.fillRect(0, 0, canvas.width / (Math.max(window.devicePixelRatio || 1, 1)), canvas.height / (Math.max(window.devicePixelRatio || 1, 1)));
    setEditForm(prev => ({ ...prev, signature: '' }));
  };

  // Add function to check if all required fields are filled
  const isFormValid = () => {
    if (!editForm) return false;
    
    const requiredFields = [
      'name',
      'email',
      'contact_number',
      'qualification',
      'course',
      'date_of_birth',
      'gender',
      'designation',
      'status'
    ];
    
    return requiredFields.every(field => editForm[field] && editForm[field].trim() !== '');
  };

  const departments = ['EKG & PHL', 'CNA', 'CPR/AED/BLS', 'Phlebotomy', 'Medical Assistant'];
  const designations = ['Instructor', 'Senior Instructor', 'Lead Instructor', 'Department Head'];
  const courses = ['EKG', 'CNA', 'CPR/AED/BLS', 'Phlebotomy', 'Medical Assistant', 'Pharmacy Technician'];

  // Function to format date of birth to readable format
  const formatDateOfBirth = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Use instructors from Redux masterData

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedInstructors(masterInstructors.map(instructor => instructor.id));
    } else {
      setSelectedInstructors([]);
    }
  };

  const handleSelectInstructor = (instructorId, isSelected) => {
    if (isSelected) {
      setSelectedInstructors(prev => [...prev, instructorId]);
    } else {
      setSelectedInstructors(prev => prev.filter(id => id !== instructorId));
    }
  };


  const handleEdit = (instructor) => {
    // Autofill course and date of birth if they exist
    // Convert ISO date string to YYYY-MM-DD format for date input
    let formattedDateOfBirth = '';
    if (instructor.date_of_birth) {
      try {
        const date = new Date(instructor.date_of_birth);
        if (!isNaN(date.getTime())) {
          formattedDateOfBirth = date.toISOString().split('T')[0];
        }
      } catch (error) {
        // Error parsing date
      }
    }

    // Find course ID that matches the course name from database
    let courseId = '';
    if (instructor.course && masterCourses && masterCourses.length > 0) {
      const matchedCourse = masterCourses.find(
        course => course.course_name === instructor.course || course.name === instructor.course
      );
      if (matchedCourse) {
        courseId = matchedCourse.id.toString();
      }
    }

    const autofilledInstructor = {
      ...instructor,
      course: courseId || instructor.course || '', // Use course ID if found, otherwise fall back to course name
      date_of_birth: formattedDateOfBirth,
      address_line1: instructor.address_line1 || '',
      address_line2: instructor.address_line2 || '',
      city: instructor.city || '',
      state: instructor.state || '',
      postal_code: instructor.postal_code || '',
      country: instructor.country || '',
      signature: instructor.signature || ''
    };
    

    setEditForm(autofilledInstructor);
    setEditApiMessage(null);
    setEditApiError(null);
    setEditModalOpen(true);
  };

  const handleDelete = async (id) => {
    const instructor = masterInstructors.find(inst => inst.id === id);
    const confirmed = await showDeleteConfirm(
      instructor?.name || 'Instructor',
      'instructor'
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/instructors/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showSuccess('Instructor deleted successfully');
        // Refresh master data after deletion
        dispatch(refreshData());
        dispatch(fetchMasterData());
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Error deleting instructor');
      }
    } catch (error) {
      showError('Error deleting instructor');
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    setEditApiMessage(null);
    setEditApiError(null);
    setIsUpdating(true);
    try {
      // Convert course ID to course name if CourseSelect was used
      let courseValue = editForm.course;
      if (courseValue && masterCourses && masterCourses.length > 0) {
        const course = masterCourses.find(c => c.id.toString() === courseValue.toString());
        if (course) {
          courseValue = course.course_name || course.name || courseValue;
        }
      }

      const data = new FormData();
      data.append('instructor_id', editForm.instructor_id);
      data.append('name', editForm.name);
      data.append('salutation', editForm.salutation);
      data.append('contact_number', editForm.contact_number);
      data.append('email', editForm.email);
      data.append('qualification', editForm.qualification);
      data.append('course', courseValue);
      data.append('date_of_birth', editForm.date_of_birth);
      data.append('gender', editForm.gender);
      data.append('marital_status', editForm.marital_status);
      data.append('designation', editForm.designation);
      data.append('status', editForm.status);
      data.append('address_line1', editForm.address_line1);
      data.append('address_line2', editForm.address_line2);
      data.append('city', editForm.city);
      data.append('state', editForm.state);
      data.append('postal_code', editForm.postal_code);
      data.append('country', editForm.country);
      if (editForm.signature) data.append('signature', editForm.signature);
      
      const res = await axios.post(`${API_BASE_URL}/staff/edit-instructor/${editForm.id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setEditApiMessage(res.data.message || 'Instructor updated successfully.');
        // Refresh master data after update
        dispatch(refreshData());
        dispatch(fetchMasterData());
        setTimeout(() => {
          setEditModalOpen(false);
          setIsUpdating(false);
        }, 2000);
      } else {
        setIsUpdating(false);
      }
    } catch (err) {
      setEditApiError(err.response?.data?.message || 'An error occurred.');
      setIsUpdating(false);
    }
  };

  return (
    <>
      <GlobalStyles />
      <Container>
        <Title>All Instructors</Title>
      
      <TableContainer>
        <TableWrapper>
          <TableHeader>
           <HeaderCell></HeaderCell>
            <HeaderCell>Instructor ID</HeaderCell>
            <HeaderCell>Instructor Name</HeaderCell>
            <HeaderCell>Email</HeaderCell>
            <HeaderCell>Designation</HeaderCell>
            <HeaderCell>Course</HeaderCell>
            <HeaderCell>Contact Number</HeaderCell>
            <HeaderCell>Date of Birth</HeaderCell>
            <HeaderCell>Gender</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Actions</HeaderCell>
          </TableHeader>
          
          <TableBody>
            {masterInstructors.map((instructor) => {
              return (
                <TableRow key={instructor.id}>
                 <CheckboxCell data-label="Select"></CheckboxCell>
                  <InstructorIdCell data-label="Instructor ID">
                    {instructor.id || 'N/A'}
                  </InstructorIdCell>
                  <NameCell data-label="Name">{instructor.name || 'N/A'}</NameCell>
                  <EmailCell data-label="Email">{instructor.email || 'N/A'}</EmailCell>
                  <TableCell data-label="Designation">{instructor.designation || 'N/A'}</TableCell>
                  <CourseCell data-label="Course">{instructor.course || 'N/A'}</CourseCell>
                  <ContactNumberCell data-label="Contact Number">{instructor.contact_number || 'N/A'}</ContactNumberCell>
                  <DateOfBirthCell data-label="Date of Birth">{formatDateOfBirth(instructor.date_of_birth)}</DateOfBirthCell>
                  <TableCell data-label="Gender">{instructor.gender || 'N/A'}</TableCell>
                  <StatusCell data-label="Status">{instructor.status || 'N/A'}</StatusCell>
                  <ActionsCell data-label="Actions">
                    <ActionButton onClick={() => handleEdit(instructor)}>Edit</ActionButton>
                    <ActionButton danger onClick={() => handleDelete(instructor.id)}>Delete</ActionButton>
                  </ActionsCell>
                </TableRow>
              );
            })}
          </TableBody>
        </TableWrapper>
      </TableContainer>

      {/* Custom Confirmation Dialog */}
      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        details={confirmState.details}
        itemName={confirmState.itemName}
      />

      {editModalOpen && editForm && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Edit Instructor</ModalTitle>
              <CloseButton onClick={() => setEditModalOpen(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              {editApiError && <div style={{ color: 'red', marginBottom: 15, padding: '10px', background: '#f8d7da', borderRadius: '4px' }}>{editApiError}</div>}
              {editApiMessage && <div style={{ color: 'green', marginBottom: 15, padding: '10px', background: '#d4edda', borderRadius: '4px' }}>{editApiMessage}</div>}
              
              <FormGrid>
                <LeftColumn>
                  <FormGroup>
                    <Label required>Instructor Name</Label>
                    <Input 
                      value={editForm.name || ''} 
                      onChange={e => handleEditInputChange('name', e.target.value)} 
                      placeholder="Enter instructor name"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Instructor ID</Label>
                    <Input 
                      value={editForm.instructor_id || ''} 
                      onChange={e => handleEditInputChange('instructor_id', e.target.value)} 
                      placeholder="Enter instructor ID"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label required>Email</Label>
                    <Input 
                      value={editForm.email || ''} 
                      onChange={e => handleEditInputChange('email', e.target.value)} 
                      placeholder="Enter email"
                      type="email"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label required>Contact Number</Label>
                    <Input 
                      value={editForm.contact_number || ''} 
                      onChange={e => handleEditInputChange('contact_number', e.target.value)} 
                      placeholder="Enter contact number"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label required>Qualification</Label>
                    <Input 
                      value={editForm.qualification || ''} 
                      onChange={e => handleEditInputChange('qualification', e.target.value)} 
                      placeholder="Enter qualification"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label required>Course</Label>
                    <CourseSelect
                      value={editForm.course || ''}
                      onChange={(value) => handleEditInputChange('course', value)}
                      placeholder="Select course"
                      isClearable={false}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label required>Date of Birth</Label>
                    <Input 
                      value={editForm.date_of_birth || ''} 
                      onChange={e => handleEditInputChange('date_of_birth', e.target.value)} 
                      type="date"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label required>Gender</Label>
                    <Select 
                      value={editForm.gender || ''} 
                      onChange={e => handleEditInputChange('gender', e.target.value)}
                    >
                      <option value="">--Select Gender--</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Others">Others</option>
                    </Select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Marital Status</Label>
                    <Select 
                      value={editForm.marital_status || ''} 
                      onChange={e => handleEditInputChange('marital_status', e.target.value)}
                    >
                      <option value="">--Select Status--</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                    </Select>
                  </FormGroup>
                </LeftColumn>
                
                <RightColumn>
                  <FormGroup>
                    <Label required>Designation</Label>
                    <Select 
                      value={editForm.designation || ''} 
                      onChange={e => handleEditInputChange('designation', e.target.value)}
                    >
                      <option value="">--Select Designation--</option>
                      {designations.map((designation, index) => (
                        <option key={index} value={designation}>{designation}</option>
                      ))}
                    </Select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label required>Status</Label>
                    <Select 
                      value={editForm.status || ''} 
                      onChange={e => handleEditInputChange('status', e.target.value)}
                    >
                      <option value="">--Select Status--</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Address Line 1</Label>
                    <Input 
                      value={editForm.address_line1 || ''} 
                      onChange={e => handleEditInputChange('address_line1', e.target.value)} 
                      placeholder="Enter address line 1"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Address Line 2</Label>
                    <Input 
                      value={editForm.address_line2 || ''} 
                      onChange={e => handleEditInputChange('address_line2', e.target.value)} 
                      placeholder="Enter address line 2"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>City</Label>
                    <Input 
                      value={editForm.city || ''} 
                      onChange={e => handleEditInputChange('city', e.target.value)} 
                      placeholder="Enter city"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>State</Label>
                    <Input 
                      value={editForm.state || ''} 
                      onChange={e => handleEditInputChange('state', e.target.value)} 
                      placeholder="Enter state"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Postal Code</Label>
                    <Input 
                      value={editForm.postal_code || ''} 
                      onChange={e => handleEditInputChange('postal_code', e.target.value)} 
                      placeholder="Enter postal code"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Country</Label>
                    <Input 
                      value={editForm.country || ''} 
                      onChange={e => handleEditInputChange('country', e.target.value)} 
                      placeholder="Enter country"
                    />
                  </FormGroup>
                </RightColumn>
              </FormGrid>

              <FormGroup>
                <Label>Signature</Label>
                <SignaturePadContainer>
                  <SignatureCanvas
                    ref={signatureCanvasRef}
                    onMouseDown={startSignatureDrawing}
                    onMouseMove={drawSignature}
                    onMouseUp={stopSignatureDrawing}
                    onMouseLeave={stopSignatureDrawing}
                  />
                  <ClearSignatureButton type="button" onClick={clearSignature}>
                    Clear Signature
                  </ClearSignatureButton>
                </SignaturePadContainer>
              </FormGroup>
              
              <ButtonGroup>
                <Button onClick={() => setEditModalOpen(false)} disabled={isUpdating}>Cancel</Button>
                <Button 
                  primary 
                  onClick={handleUpdate}
                  disabled={!isFormValid() || isUpdating}
                  style={{ 
                    opacity: (isFormValid() && !isUpdating) ? 1 : 0.6, 
                    cursor: (isFormValid() && !isUpdating) ? 'pointer' : 'not-allowed',
                    position: 'relative'
                  }}
                >
                  {isUpdating ? (
                    <>
                      <span style={{ marginRight: '8px' }}>Updating...</span>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid #ffffff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></span>
                    </>
                  ) : (
                    'Update Instructor'
                  )}
                </Button>

              </ButtonGroup>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
      </Container>
    </>
  );
};

export default AllInstructors; 